import type {SelectedServer, SelectedOrganization} from './types'

export class AllowlistDialogState {
  #addedServers: SelectedServer[] = []
  #originalServers: SelectedServer[] = []
  #removedServers: SelectedServer[] = []
  #currentSelectedServer: SelectedServer | null = null
  #selectedOrganizations: SelectedOrganization[] = []

  get addedServers(): readonly SelectedServer[] {
    return this.#addedServers
  }

  get originalServers(): readonly SelectedServer[] {
    return this.#originalServers
  }

  get removedServers(): readonly SelectedServer[] {
    return this.#removedServers
  }

  get currentSelectedServer(): SelectedServer | null {
    return this.#currentSelectedServer
  }

  get selectedOrganizations(): readonly SelectedOrganization[] {
    return this.#selectedOrganizations
  }

  setCurrentSelectedServer(server: SelectedServer | null): void {
    this.#currentSelectedServer = server
  }

  setOriginalServers(servers: SelectedServer[]): void {
    this.#originalServers = servers.map(s => ({...s}))
  }

  addServer(server: SelectedServer): boolean {
    const alreadyAdded = this.#addedServers.some(s => s.id === server.id)
    if (!alreadyAdded) {
      this.#addedServers.push({...server})
      // If re-adding a previously removed server, un-remove it
      const removedIndex = this.#removedServers.findIndex(s => s.id === server.id)
      if (removedIndex >= 0) {
        this.#removedServers.splice(removedIndex, 1)
      }
      return true
    }
    return false
  }

  removeServerAtIndex(index: number): boolean {
    if (index >= 0 && index < this.#addedServers.length) {
      const removed = this.#addedServers.splice(index, 1)[0]
      // Track removal only if the server was originally present (pre-existing)
      if (removed && this.#originalServers.some(s => s.id === removed.id)) {
        this.#removedServers.push(removed)
      }
      return true
    }
    return false
  }

  addOrganization(org: SelectedOrganization): void {
    if (!this.#selectedOrganizations.some(o => o.id === org.id)) {
      this.#selectedOrganizations.push({...org})
    }
  }

  removeOrganization(orgId: string): void {
    const index = this.#selectedOrganizations.findIndex(o => o.id === orgId)
    if (index >= 0) {
      this.#selectedOrganizations.splice(index, 1)
    }
  }

  reset(): void {
    this.#addedServers.length = 0
    this.#originalServers.length = 0
    this.#removedServers.length = 0
    this.#currentSelectedServer = null
    this.#selectedOrganizations.length = 0
  }
}

// Global state instance — needed because delegated-events handlers share state
let dialogState: AllowlistDialogState | null = null

export function getDialogState(): AllowlistDialogState {
  if (!dialogState) {
    dialogState = new AllowlistDialogState()
  }
  return dialogState
}

export function resetDialogState(): void {
  if (dialogState) {
    dialogState.reset()
  }
}
